import { useCallback, useRef } from "react";
import { useLocation, useSearchParams } from "wouter";

type SearchParamsInit = ConstructorParameters<typeof URLSearchParams>[0];
type SearchParamsSetter = (
  nextInit: SearchParamsInit | ((prev: URLSearchParams) => SearchParamsInit),
  options?: { replace?: boolean; state?: unknown },
) => void;

export function useSearchParamsWithoutEmptyQuery(): [
  URLSearchParams,
  SearchParamsSetter,
] {
  const [location, navigate] = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingSearchParamsRef = useRef(searchParams);
  pendingSearchParamsRef.current = searchParams;

  const setSearchParamsWithoutEmptyQuery = useCallback<SearchParamsSetter>(
    (nextInit, options) => {
      const previous = new URLSearchParams(pendingSearchParamsRef.current);
      const next = new URLSearchParams(
        typeof nextInit === "function" ? nextInit(previous) : nextInit,
      );
      pendingSearchParamsRef.current = next;

      if (next.toString() === "") {
        navigate(location, options);
        return;
      }

      setSearchParams(next, options);
    },
    [location, navigate, setSearchParams],
  );

  return [searchParams, setSearchParamsWithoutEmptyQuery];
}
